import { ExtensionRegistry } from '@doxyme/extensions-bridge';
import { isProduction } from '../../../shared/isProduction';
import { addPropertyToWindow } from '../../../shared/addPropertyToWindow';
import { getLogger } from '../../../libs/logger';
import { getResolvedFrontendGraphqlUrls } from '../../../services/resolveFrontendGraphqlUrls';

const logger = getLogger('instances/registry', {
  component: 'extensions',
  lib: 'frontend',
});

export type RequiredRegistryProps = {
  apiUrl: string;
};

export const getApiUrl = (): string => {
  try {
    return getResolvedFrontendGraphqlUrls().graphqlUrl;
  } catch (error) {
    logger.error('Failed to resolve frontend GraphQL URL', error);
    return '';
  }
};

export const registry = new ExtensionRegistry(getApiUrl());

type InstallAppFn = (extensionId: string) => Promise<void>;

export type InstallAppResult = {
  success: boolean;
  error?: string;
};

/** Exposed on `window.Registry` for external tools to install apps. */
export type RegistryWithInstall = {
  raw: ExtensionRegistry;
  installApp: (extensionId: string) => Promise<InstallAppResult>;
  isReady: () => boolean;
  setInstallApp: (fn: InstallAppFn) => void;
  clearInstallApp: () => void;
};

let _installAppFn: InstallAppFn | undefined;

const safeInstallApp = async (
  extensionId: string,
): Promise<InstallAppResult> => {
  if (!_installAppFn) {
    const error = 'Registry not ready. User may not be authenticated.';
    logger.error(error);
    return { success: false, error };
  }

  try {
    await _installAppFn(extensionId);
    return { success: true };
  } catch (err) {
    const error = err instanceof Error ? err.message : 'Unknown error';
    logger.error('installApp failed', { extensionId, error });
    return { success: false, error };
  }
};

export const registryWithInstall: RegistryWithInstall = {
  raw: registry,
  installApp: safeInstallApp,
  isReady: (): boolean => _installAppFn !== undefined,
  setInstallApp: (fn: InstallAppFn): void => {
    _installAppFn = fn;
    logger.info('Registry.installApp registered');
  },
  clearInstallApp: (): void => {
    _installAppFn = undefined;
    logger.info('Registry.installApp cleared');
  },
};

/**
 * Exposes `window.Registry` for external tools to install apps.
 *
 * @example
 * ```javascript
 * const result = await window.Registry.installApp('extension-uuid');
 * if (result.success) {
 *   console.log('App installed');
 * } else {
 *   console.error(result.error);
 * }
 * ```
 *
 * Dev: full object with `raw`. Prod: only `installApp` and `isReady`.
 */
export const addRegistryToWindow = (): void => {
  if (!isProduction()) {
    addPropertyToWindow<RegistryWithInstall>('Registry', registryWithInstall);
  } else {
    addPropertyToWindow<Pick<RegistryWithInstall, 'installApp' | 'isReady'>>(
      'Registry',
      {
        installApp: registryWithInstall.installApp,
        isReady: registryWithInstall.isReady,
      },
    );
  }
};

addRegistryToWindow();
